import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SIDEBAR_WIDTH = 'clamp(220px, 20vw, 310px)';
const DOT_COL_PX = 28; // fixed-width dot column in px
const LINE_LEFT = DOT_COL_PX / 2; // center of dot column

const TimelineSidebar = ({ months, onMonthClick, allLocations, allDiveCenters }) => {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef(null);

  const locationColors = [
    { dot: 'bg-cyan-400', glow: 'shadow-cyan-400/50', text: 'text-cyan-300', line: 'bg-cyan-400/30', hex: '#22d3ee' },
    { dot: 'bg-purple-400', glow: 'shadow-purple-400/50', text: 'text-purple-300', line: 'bg-purple-400/30', hex: '#c084fc' },
    { dot: 'bg-emerald-400', glow: 'shadow-emerald-400/50', text: 'text-emerald-300', line: 'bg-emerald-400/30', hex: '#34d399' },
    { dot: 'bg-orange-400', glow: 'shadow-orange-400/50', text: 'text-orange-300', line: 'bg-orange-400/30', hex: '#fb923c' },
    { dot: 'bg-amber-400', glow: 'shadow-amber-400/50', text: 'text-amber-300', line: 'bg-amber-400/30', hex: '#fbbf24' },
    { dot: 'bg-indigo-400', glow: 'shadow-indigo-400/50', text: 'text-indigo-300', line: 'bg-indigo-400/30', hex: '#818cf8' },
  ];

  const getLocationColor = (locationId) => {
    if (!locationId) return null;
    const locIndex = allLocations.findIndex(l => l.id === locationId);
    if (locIndex >= 0) return locationColors[locIndex % locationColors.length];
    const centerIndex = allDiveCenters.findIndex(c => c.id === locationId);
    if (centerIndex >= 0) return locationColors[centerIndex % locationColors.length];
    return locationColors[0];
  };

  // When expanded, scroll so current month is ~40% from top
  useEffect(() => {
    if (expanded && listRef.current) {
      const currentIdx = months.findIndex(m => m.is_current);
      if (currentIdx < 0) return;
      requestAnimationFrame(() => {
        const el = listRef.current;
        if (!el) return;
        const items = el.querySelectorAll('[data-month-item]');
        if (!items[currentIdx]) return;
        const itemTop = items[currentIdx].offsetTop;
        const containerH = el.clientHeight;
        el.scrollTo({ top: Math.max(itemTop - containerH * 0.4, 0), behavior: 'smooth' });
      });
    }
  }, [expanded, months]);

  const handleMonthClick = (month) => {
    onMonthClick(month);
    setExpanded(false);
  };

  if (!months.length) return null;

  return (
    <div
      className="fixed left-0 top-0 h-full z-[11] flex transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        transform: expanded
          ? 'translateX(0)'
          : `translateX(calc(-1 * ${SIDEBAR_WIDTH}))`,
      }}
      data-testid="timeline-sidebar-wrapper"
    >
      {/* ── Sidebar panel ── */}
      <div
        className="h-full relative shrink-0"
        style={{ width: SIDEBAR_WIDTH }}
        data-testid="timeline-sidebar"
      >
        {/* Opaque backdrop — blocks content underneath completely */}
        <div className="absolute inset-0 bg-[#0A0A14] backdrop-blur-xl" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A0A14]/0" style={{ right: '-1px' }} />

        {/* Scrollable content */}
        <div className="absolute inset-0 flex flex-col pt-24 pb-6">
          {/* Fade edges */}
          <div className="pointer-events-none absolute top-24 left-0 right-0 h-12 bg-gradient-to-b from-[#0A0A14]/60 to-transparent z-[1]" />
          <div className="pointer-events-none absolute bottom-6 left-0 right-0 h-12 bg-gradient-to-t from-[#0A0A14]/60 to-transparent z-[1]" />

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto pl-5 sm:pl-6 md:pl-8 pr-3 scrollbar-hidden"
          >
            {/* relative wrapper so the continuous line can be positioned */}
            <div className="relative py-6 pb-12">
              {/* ── Continuous vertical line ── */}
              {/* Runs from center of first dot to center of last dot */}
              <div
                className="absolute w-[2px] bg-white/10 rounded-full"
                style={{
                  left: `${LINE_LEFT - 1}px`,
                  top: 0,
                  bottom: 0,
                }}
              />

              {/* Process months to detect shared month entries */}
              {(() => {
                // Group entries by month+year
                const grouped = [];
                const visited = new Set();
                months.forEach((month, idx) => {
                  if (visited.has(idx)) return;
                  const key = `${month.month}-${month.year}`;
                  const partners = [];
                  months.forEach((m, j) => {
                    if (j !== idx && !visited.has(j) && `${m.month}-${m.year}` === key) {
                      partners.push({ ...m, _origIdx: j });
                      visited.add(j);
                    }
                  });
                  visited.add(idx);
                  grouped.push({
                    primary: { ...month, _origIdx: idx },
                    partners,
                  });
                });

                return grouped.map((group, gIdx) => {
                  const month = group.primary;
                  const idx = month._origIdx;
                  const isCurrent = month.is_current || group.partners.some(p => p.is_current);
                  const hasLocation = (month.location_id || month.dive_center_id) && month.location_name;
                  const locationColor = getLocationColor(month.location_id || month.dive_center_id);
                  const hasPartner = group.partners.length > 0 && group.partners[0].location_name;
                  const partnerColor = hasPartner ? getLocationColor(group.partners[0].location_id || group.partners[0].dive_center_id) : null;

                  // Build day label
                  const dayLabel = (entry) => {
                    if (entry.start_day && entry.end_day) return `${entry.start_day}-${entry.end_day}`;
                    if (entry.start_day) return `from ${entry.start_day}`;
                    if (entry.end_day) return `until ${entry.end_day}`;
                    return '';
                  };

                  return (
                    <div key={gIdx} className="relative" data-month-item>
                      {/* Colored line overlay segment */}
                      {gIdx < grouped.length - 1 && hasLocation && locationColor && (
                        <div
                          className={`absolute w-[2px] rounded-full ${locationColor.line}`}
                          style={{
                            left: `${LINE_LEFT - 1}px`,
                            top: '50%',
                            height: '100%',
                            zIndex: 1,
                          }}
                        />
                      )}

                      <button
                        onClick={() => hasLocation && handleMonthClick(month)}
                        className={`w-full flex items-center py-2.5 sm:py-3 md:py-3.5 group ${
                          hasLocation ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        data-testid={`timeline-month-${month.month}-${month.year}`}
                      >
                        {/* Fixed-width dot column */}
                        <div
                          className="shrink-0 flex items-center justify-center relative z-[2]"
                          style={{ width: `${DOT_COL_PX}px` }}
                        >
                          {hasPartner && partnerColor ? (
                            // Split dot: two colors
                            <div
                              className={`rounded-full transition-all duration-300 ${
                                isCurrent
                                  ? 'w-6 h-6 sm:w-7 sm:h-7 shadow-lg ring-2 ring-white/30'
                                  : 'w-4 h-4 sm:w-[18px] sm:h-[18px] group-hover:scale-[1.3] group-hover:shadow-md'
                              }`}
                              style={{
                                background: `linear-gradient(135deg, ${locationColor?.hex || '#22d3ee'} 50%, ${partnerColor?.hex || '#c084fc'} 50%)`,
                              }}
                            />
                          ) : (month.start_day || month.end_day) && hasLocation ? (
                            // Partial month dot: portion filled
                            (() => {
                              const start = month.start_day || 1;
                              const end = month.end_day || 31;
                              const fillPercent = Math.round(((end - start + 1) / 31) * 100);
                              return (
                                <div
                                  className={`rounded-full transition-all duration-300 overflow-hidden ${
                                    isCurrent
                                      ? 'w-6 h-6 sm:w-7 sm:h-7 shadow-lg ring-2 ring-white/30'
                                      : 'w-4 h-4 sm:w-[18px] sm:h-[18px] group-hover:scale-[1.3] group-hover:shadow-md'
                                  }`}
                                  style={{
                                    background: `linear-gradient(to bottom, ${locationColor?.hex || '#22d3ee'} ${fillPercent}%, rgba(255,255,255,0.15) ${fillPercent}%)`,
                                  }}
                                />
                              );
                            })()
                          ) : (
                            // Normal solid dot
                            <div className={`rounded-full transition-all duration-300 ${
                              isCurrent
                                ? `w-6 h-6 sm:w-7 sm:h-7 ${locationColor?.dot || 'bg-cyan-400'} shadow-lg ${locationColor?.glow || 'shadow-cyan-400/50'} ring-2 ring-white/30`
                                : hasLocation
                                ? `w-4 h-4 sm:w-[18px] sm:h-[18px] ${locationColor?.dot || 'bg-white/40'} group-hover:scale-[1.3] group-hover:shadow-md`
                                : 'w-3.5 h-3.5 sm:w-4 sm:h-4 bg-white/20'
                            }`} />
                          )}
                        </div>

                        {/* Labels */}
                        <div className="flex flex-col min-w-0 ml-3 sm:ml-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-base sm:text-lg md:text-xl font-bold tracking-wide transition-colors ${
                              isCurrent ? 'text-white' : hasLocation ? 'text-white/80 group-hover:text-white' : 'text-white/30'
                            }`}>
                              {month.month}
                            </span>
                            <span className={`text-sm sm:text-base ${isCurrent ? 'text-white/60' : 'text-white/25'}`}>
                              {month.year}
                            </span>
                            {isCurrent && (
                              <span className="relative flex items-center">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-30" />
                                <span className="relative px-2.5 py-1 bg-cyan-500/60 backdrop-blur-sm text-[9px] sm:text-[10px] font-bold text-white rounded-full uppercase tracking-widest">
                                  Now
                                </span>
                              </span>
                            )}
                          </div>
                          {/* Primary location */}
                          {hasLocation && (
                            <span className={`text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px] md:max-w-[180px] transition-colors ${
                              isCurrent
                                ? `${locationColor?.text || 'text-cyan-300'}`
                                : 'text-white/30 group-hover:text-white/60'
                            }`}>
                              {month.location_name.split(',')[0]}
                              {dayLabel(month) && (
                                <span className="text-[10px] opacity-60 ml-1">({dayLabel(month)})</span>
                              )}
                            </span>
                          )}
                          {/* Partner location (shared month) */}
                          {hasPartner && (
                            <span className={`text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px] md:max-w-[180px] transition-colors ${
                              isCurrent
                                ? `${partnerColor?.text || 'text-purple-300'}`
                                : 'text-white/20 group-hover:text-white/50'
                            }`}>
                              {group.partners[0].location_name.split(',')[0]}
                              {dayLabel(group.partners[0]) && (
                                <span className="text-[10px] opacity-60 ml-1">({dayLabel(group.partners[0])})</span>
                              )}
                            </span>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* ── Toggle button — attached to sidebar's right edge ── */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="self-center shrink-0 backdrop-blur-xl text-white/80 hover:text-white px-2.5 py-7 rounded-r-xl shadow-xl transition-all duration-300 border border-l-0"
        style={{ 
          backgroundColor: 'rgba(10,10,20,0.85)',
          borderColor: 'rgba(162,133,212,0.2)'
        }}
        data-testid="timeline-sidebar-toggle-btn"
        aria-label={expanded ? 'Collapse schedule' : 'Show schedule'}
      >
        {expanded
          ? <ChevronLeft className="w-5 h-5 mb-2" />
          : <ChevronRight className="w-5 h-5 mb-2" />
        }
        <span
          className="text-[10px] font-semibold tracking-widest uppercase"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          Schedule
        </span>
      </button>
    </div>
  );
};

export default TimelineSidebar;
